/**
 * What the player sees when something breaks.
 *
 * Until this existed, every unhandled error looked identical from the outside: a
 * black screen, sometimes with the instruments still drawn over it, and nothing
 * to say whether the game had crashed, rendered nothing, or quietly put the ship
 * somewhere there was nothing to see. Three separate faults this week presented
 * that way and each took a session to tell apart.
 *
 * That is survivable with the source open next to you. It is useless in front of
 * a tester, who can only report "it went grey".
 *
 * ── Two kinds of error, and a boundary only catches one ─────────────────────
 *
 * React error boundaries catch what happens during render, in lifecycle, and in
 * effects — which is real coverage, and would have caught the ship editor
 * throwing on mount.
 *
 * It would **not** have caught anything in the flight loop, and that is where
 * nearly all of this game's code runs. The loop is a `requestAnimationFrame`
 * chain outside React entirely, so React never sees the throw. Worse, every loop
 * here re-schedules on its *first* line, so a fault does not even stop it: it
 * throws again on the next frame, sixty times a second, filling the console with
 * copies of one error while the screen sits there saying nothing.
 *
 * So the window is listened to as well, and the first error wins. Sixty
 * identical reports a second is not sixty faults, and the useful one is the
 * first — everything after it is a consequence.
 *
 * ── It shows the error on purpose ───────────────────────────────────────────
 *
 * Stack and all. This is a test build and the person looking at it is trying to
 * report a bug, not be reassured — so the panel is built around getting the text
 * *out*, which is what the copy button is for.
 */

import { Component, type ErrorInfo, type ReactNode } from 'react';

const HUD_TEXT = '#dbeaff';
const HUD_DIM = 'rgba(219, 234, 255, 0.55)';
const HUD_RED = '#ff5f6b';

export interface Crash {
  message: string;
  stack: string;
  /** Where it came from, which narrows the search more than anything else. */
  origin: 'render' | 'loop' | 'promise';
}

interface ErrorBoundaryState {
  crash: Crash | null;
}

/** Everything worth pasting into a bug report, as one block of text. */
function report(crash: Crash): string {
  return [
    `Interspacial crash (${crash.origin})`,
    `when: ${new Date().toISOString()}`,
    `where: ${window.location.href}`,
    '',
    crash.message,
    '',
    crash.stack,
  ].join('\n');
}

export class ErrorBoundary extends Component<{ children: ReactNode }, ErrorBoundaryState> {
  override state: ErrorBoundaryState = { crash: null };

  static getDerivedStateFromError(error: unknown): ErrorBoundaryState {
    return { crash: describe(error, 'render') };
  }

  override componentDidCatch(error: unknown, info: ErrorInfo): void {
    // The component stack is the part a boundary knows that the error itself
    // does not — which component was rendering when it went.
    console.error('[crash] render', error, info.componentStack);
  }

  override componentDidMount(): void {
    window.addEventListener('error', this.onWindowError);
    window.addEventListener('unhandledrejection', this.onRejection);
  }

  override componentWillUnmount(): void {
    window.removeEventListener('error', this.onWindowError);
    window.removeEventListener('unhandledrejection', this.onRejection);
  }

  /** The first one only. See the note about loops re-scheduling. */
  private readonly take = (crash: Crash): void => {
    if (this.state.crash) return;
    this.setState({ crash });
  };

  private readonly onWindowError = (event: ErrorEvent): void => {
    this.take(describe(event.error ?? event.message, 'loop'));
  };

  private readonly onRejection = (event: PromiseRejectionEvent): void => {
    this.take(describe(event.reason, 'promise'));
  };

  override render(): ReactNode {
    const { crash } = this.state;
    if (!crash) return this.props.children;

    return (
      <div
        style={{
          position: 'fixed',
          inset: 0,
          background: '#05070d',
          color: HUD_TEXT,
          font: "13px 'Syne Mono', ui-monospace, monospace",
          padding: '32px clamp(16px, 6vw, 80px)',
          overflow: 'auto',
          zIndex: 9999,
        }}
      >
        <div style={{ fontSize: 22, letterSpacing: 2, color: HUD_RED, marginBottom: 6 }}>
          SOMETHING BROKE
        </div>
        <div style={{ color: HUD_DIM, marginBottom: 20 }}>
          The game stopped rather than carried on wrong. Nothing you own is lost — your ship,
          cargo and linelits live on the server, not in this page.
        </div>

        <div style={{ color: HUD_DIM, fontSize: 11, letterSpacing: 1.4, marginBottom: 4 }}>
          {crash.origin === 'render'
            ? 'WHILE DRAWING THE SCREEN'
            : crash.origin === 'promise'
              ? 'IN A BACKGROUND REQUEST'
              : 'IN THE FLIGHT LOOP'}
        </div>
        <div style={{ color: HUD_RED, marginBottom: 16, wordBreak: 'break-word' }}>
          {crash.message}
        </div>

        <pre
          style={{
            margin: 0,
            padding: 12,
            maxHeight: '38vh',
            overflow: 'auto',
            background: 'rgba(8, 14, 24, 0.82)',
            border: '1px solid rgba(127, 212, 255, 0.22)',
            color: HUD_DIM,
            fontSize: 11,
            lineHeight: 1.5,
            whiteSpace: 'pre-wrap',
          }}
        >
          {crash.stack}
        </pre>

        <div style={{ display: 'flex', gap: 10, marginTop: 18, flexWrap: 'wrap' }}>
          <button type="button" style={ACTION} onClick={() => void copy(report(crash))}>
            copy details
          </button>
          <button type="button" style={ACTION} onClick={() => window.location.reload()}>
            reload
          </button>
        </div>

        <div style={{ color: HUD_DIM, fontSize: 11, marginTop: 14 }}>
          Reloading is safe and usually enough. If it happens again in the same place, the copied
          details are the useful part of the report.
        </div>
      </div>
    );
  }
}

const ACTION: React.CSSProperties = {
  background: 'transparent',
  border: '1px solid rgba(127, 212, 255, 0.45)',
  color: HUD_TEXT,
  font: 'inherit',
  padding: '8px 16px',
  cursor: 'pointer',
};

/**
 * Anything at all, turned into something a person can read.
 *
 * Not everything thrown is an `Error` — a rejected fetch can carry a string, and
 * `window.onerror` may have nothing but a message. A crash screen that says
 * `[object Object]` is barely better than the black one it replaced.
 */
function describe(thrown: unknown, origin: Crash['origin']): Crash {
  if (thrown instanceof Error) {
    return {
      message: `${thrown.name}: ${thrown.message}`,
      stack: thrown.stack ?? '(no stack)',
      origin,
    };
  }

  return {
    message: typeof thrown === 'string' ? thrown : JSON.stringify(thrown) || String(thrown),
    stack: '(no stack — what was thrown was not an Error)',
    origin,
  };
}

/**
 * Clipboard, with a fallback.
 *
 * `navigator.clipboard` needs a secure context, and the dev build is served over
 * plain HTTP — which is exactly where this screen gets used most.
 */
async function copy(text: string): Promise<void> {
  try {
    await navigator.clipboard.writeText(text);
  } catch {
    const box = document.createElement('textarea');
    box.value = text;
    box.style.cssText = 'position:fixed;opacity:0';
    document.body.appendChild(box);
    box.select();
    document.execCommand('copy');
    box.remove();
  }
}
